import { useState } from "react";
import { NAVY, WHITE } from "../styles/palette";
import { getAuthToken, SUPABASE_ANON_KEY, SUPABASE_URL } from "../lib/supabase";
import { useIsMobile } from "../lib/useIsMobile";

const CHARGE_REASONS = [
  "Extra cleanup",
  "One-time service",
  "Late fee",
  "Deodorizer treatment",
  "Special request",
];

export function ChargeClientModal({ client, invoices, onClose, onSuccess, showToast }) {
  const isMobile = useIsMobile();
  // Eligible invoices: draft + open, with a Stripe ID (so we can charge them
  // via finalize-invoice). Sort newest first so the most likely target is on top.
  const eligibleInvoices = (invoices || [])
    .filter(i => (i.status === "draft" || i.status === "open") && i.stripe_invoice_id)
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  // Default mode: pay-existing when there's something to pay, else new-charge
  const [mode, setMode] = useState(eligibleInvoices.length > 0 ? "existing" : "new");
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(eligibleInvoices[0]?.id || null);

  const [step,        setStep]        = useState(1); // new-charge: 1=form, 2=review
  const [reason,      setReason]      = useState(CHARGE_REASONS[0]);
  const [notes,       setNotes]       = useState("");
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState("");
  const [draftInv,    setDraftInv]    = useState(null); // Stripe draft invoice
  const [lineItems,   setLineItems]   = useState([{description:"",amount:""}]);
  const [finalizing,  setFinalizing]  = useState(false);

  const total = lineItems.reduce((s,i)=>s+Number(i.amount||0),0);
  const selectedInvoice = eligibleInvoices.find(i => i.id === selectedInvoiceId);
  const goldBtn = isMobile ? {flex:"1 1 100%",justifyContent:"center"} : undefined;

  // Charge an already-existing draft/open invoice (no new line items
  // created — pays exactly what's on the invoice in Stripe).
  async function handleChargeExisting() {
    if (!selectedInvoice) { setError("Pick an invoice to charge"); return; }
    if (!window.confirm(`Charge $${Number(selectedInvoice.amount).toFixed(2)} for the ${selectedInvoice.invoice_number || selectedInvoice.date || ""} invoice? This is immediate and irreversible.`)) return;
    setFinalizing(true); setError("");
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/finalize-invoice`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ stripe_invoice_id: selectedInvoice.stripe_invoice_id, client_id: client.id }),
      }).then(r => r.json());
      if (res.error) { setError(res.error); setFinalizing(false); return; }
      showToast(`Charged $${Number(selectedInvoice.amount).toFixed(2)} to ${client.name}!`);
      onSuccess(selectedInvoice);
    } catch(e) { setError("Charge failed — check Stripe dashboard"); }
    setFinalizing(false);
  }

  async function handleCreateDraft() {
    setError("");
    const validItems = lineItems.filter(i=>i.description&&Number(i.amount)>0);
    if (!validItems.length) { setError("Add at least one line item with a description and amount"); return; }
    setSaving(true);
    try {
      // Create draft invoice in Stripe via edge function
      const res = await fetch(`${SUPABASE_URL}/functions/v1/create-misc-invoice`,{
        method:"POST",
        headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
        body:JSON.stringify({
          client_id:  client.id,
          line_items: validItems,
          notes,
          reason,
        })
      }).then(r=>r.json());
      if (res.error) { setError(res.error); setSaving(false); return; }
      setDraftInv(res.invoice);
      setStep(2);
    } catch(e) { setError("Failed to create draft — please try again"); }
    setSaving(false);
  }

  async function handleFinalizeAndCharge() {
    if (!draftInv) return;
    setFinalizing(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/finalize-invoice`,{
        method:"POST",
        headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
        body:JSON.stringify({ stripe_invoice_id:draftInv.stripe_invoice_id, client_id:client.id })
      }).then(r=>r.json());
      if (res.error) { setError(res.error); setFinalizing(false); return; }
      showToast(`Charged $${total.toFixed(2)} to ${client.name} successfully!`);
      onSuccess(draftInv);
    } catch(e) { setError("Charge failed — check Stripe dashboard"); }
    setFinalizing(false);
  }

  return (
    <div className="modal-overlay" onClick={async e=>{
      if (e.target!==e.currentTarget) return;
      if (step===2&&draftInv?.stripe_invoice_id) {
        await fetch(`${SUPABASE_URL}/functions/v1/update-invoice`,{
          method:"POST",
          headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
          body:JSON.stringify({action:"void",stripe_invoice_id:draftInv.stripe_invoice_id})
        }).catch(()=>{});
        if (draftInv.id) await fetch(`${SUPABASE_URL}/rest/v1/invoices?id=eq.${draftInv.id}`,{
          method:"DELETE",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
        }).catch(()=>{});
      }
      onClose();
    }}>
      <div className="modal" style={{maxWidth:500}}>
        <div className="modal-header">
          <h3>{step===1?"💳 New Charge":"📋 Review & Charge"} — {client.name}</h3>
          <button className="close-btn" onClick={async()=>{
            if (step===2&&draftInv?.stripe_invoice_id) {
              await fetch(`${SUPABASE_URL}/functions/v1/update-invoice`,{
                method:"POST",
                headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
                body:JSON.stringify({action:"void",stripe_invoice_id:draftInv.stripe_invoice_id})
              }).catch(()=>{});
              if (draftInv.id) await fetch(`${SUPABASE_URL}/rest/v1/invoices?id=eq.${draftInv.id}`,{
                method:"DELETE",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
              }).catch(()=>{});
            }
            onClose();
          }}>×</button>
        </div>
        <div className="modal-body">
          {/* Card on file */}
          <div style={{background:"linear-gradient(135deg,"+NAVY+",#1547b5)",borderRadius:8,padding:"10px 16px",color:WHITE,marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div>
              <div style={{fontSize:11,opacity:.6,marginBottom:2}}>Card on file</div>
              <div style={{fontWeight:600,fontSize:14,letterSpacing:2}}>•••• •••• •••• {client.stripe_pm_last4||"????"}</div>
              <div style={{fontSize:11,opacity:.6,marginTop:2,textTransform:"capitalize"}}>{client.stripe_pm_brand||"card"}</div>
            </div>
            <div style={{fontSize:24}}>💳</div>
          </div>

          {step===1&&(
            <>
              {/* Mode tabs — only show when client has eligible invoices */}
              {eligibleInvoices.length > 0 && (
                <div style={{display:"flex",gap:0,marginBottom:14,borderBottom:"2px solid #e3e8ef"}}>
                  {/* Phones: each mode tab fills half the width at ≥40px tall. */}
                  <div onClick={()=>setMode("existing")}
                    style={{padding:isMobile?"12px 10px":"8px 14px",flex:isMobile?1:undefined,textAlign:isMobile?"center":undefined,cursor:"pointer",fontSize:13,fontWeight:600,color:mode==="existing"?NAVY:"#8993a4",borderBottom:mode==="existing"?"2px solid "+NAVY:"2px solid transparent",marginBottom:-2}}>
                    Pay Existing Invoice ({eligibleInvoices.length})
                  </div>
                  <div onClick={()=>setMode("new")}
                    style={{padding:isMobile?"12px 10px":"8px 14px",flex:isMobile?1:undefined,textAlign:isMobile?"center":undefined,cursor:"pointer",fontSize:13,fontWeight:600,color:mode==="new"?NAVY:"#8993a4",borderBottom:mode==="new"?"2px solid "+NAVY:"2px solid transparent",marginBottom:-2}}>
                    New Charge
                  </div>
                </div>
              )}

              {/* Pay-existing mode */}
              {mode==="existing" && (
                <div>
                  <label style={{fontSize:12,fontWeight:600,color:"#42526e",display:"block",marginBottom:8}}>Select invoice to charge</label>
                  {eligibleInvoices.map(inv => {
                    const selected = inv.id === selectedInvoiceId;
                    return (
                      <div key={inv.id} onClick={()=>setSelectedInvoiceId(inv.id)}
                        style={{
                          border: selected ? `2px solid ${NAVY}` : "1px solid #e3e8ef",
                          background: selected ? "#f0f4ff" : "#fff",
                          borderRadius: 8, padding: "10px 12px", marginBottom: 6, cursor: "pointer",
                          display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
                        }}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontWeight:600,fontSize:13,color:"#1a1f36"}}>
                            {inv.invoice_number || `Invoice · ${inv.date || ""}`}
                            <span style={{fontSize:10,fontWeight:700,padding:"1px 6px",borderRadius:10,marginLeft:8,textTransform:"uppercase",letterSpacing:".04em",background:inv.status==="draft"?"#eae6ff":"#fffae6",color:inv.status==="draft"?"#403294":"#974f0c"}}>
                              {inv.status}
                            </span>
                          </div>
                          <div style={{fontSize:11,color:"#8993a4",marginTop:2}}>{inv.date}{inv.period ? ` · ${inv.period}` : ""}</div>
                          {Array.isArray(inv.line_items) && inv.line_items.length > 0 && (
                            <div style={{fontSize:11,color:"#42526e",marginTop:4}}>
                              {inv.line_items.map(li => li.description).join(" · ")}
                            </div>
                          )}
                        </div>
                        <div style={{fontWeight:800,fontSize:18,color:NAVY,flexShrink:0}}>${Number(inv.amount).toFixed(2)}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* New-charge mode (original UI) */}
              {mode==="new" && (
                <>
              <div className="form-group" style={{marginBottom:12}}>
                <label>Charge Reason</label>
                <select value={reason} onChange={e=>setReason(e.target.value)}>
                  {CHARGE_REASONS.map(r=><option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              {/* Line items */}
              <div style={{marginBottom:12}}>
                <label style={{fontSize:12,fontWeight:600,color:"#42526e",display:"block",marginBottom:8}}>Line Items</label>
                {lineItems.map((item,i)=>(
                  <div key={i} style={{display:"grid",gridTemplateColumns:isMobile?"minmax(0,1fr) 88px 36px":"1fr 100px 32px",gap:6,marginBottom:6}}>
                    <input placeholder="Description" value={item.description}
                      onChange={e=>setLineItems(li=>li.map((x,j)=>j===i?{...x,description:e.target.value}:x))}
                      style={{fontSize:13,minWidth:0}}/>
                    <input type="number" placeholder="$0.00" value={item.amount} min="0" step="0.01"
                      onChange={e=>setLineItems(li=>li.map((x,j)=>j===i?{...x,amount:e.target.value}:x))}
                      style={{fontSize:13,textAlign:"right",minWidth:0}}/>
                    {lineItems.length>1
                      ? <button onClick={()=>setLineItems(li=>li.filter((_,j)=>j!==i))} style={{background:"#ffebe6",border:"none",borderRadius:4,color:"#bf2600",cursor:"pointer",fontSize:isMobile?18:14,minHeight:isMobile?36:undefined}}>×</button>
                      : <div/>}
                  </div>
                ))}
                <button className="btn btn-ghost btn-sm" style={{fontSize:11,marginTop:4}}
                  onClick={()=>setLineItems(li=>[...li,{description:"",amount:""}])}>
                  + Add Line Item
                </button>
              </div>

              <div className="form-group" style={{marginBottom:12}}>
                <label>Notes (optional)</label>
                <textarea value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Additional notes..." style={{minHeight:50}}/>
              </div>

              {total>0&&(
                <div style={{background:"#f0f4ff",border:"1px solid #c7d2fe",borderRadius:8,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{fontSize:12,color:"#42526e"}}>Total</div>
                  <div style={{fontWeight:800,fontSize:22,color:NAVY}}>${total.toFixed(2)}</div>
                </div>
              )}
                </>
              )}
            </>
          )}

          {step===2&&draftInv&&(
            <>
              <div style={{background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:12,color:"#065F46"}}>
                ✓ Draft invoice created in Stripe — review below before charging
              </div>

              {/* Line items review */}
              <table style={{width:"100%",borderCollapse:"collapse",marginBottom:12}}>
                <thead>
                  <tr style={{borderBottom:"2px solid #e3e8ef"}}>
                    <th style={{textAlign:"left",padding:"6px 8px",fontSize:11,color:"#8993a4"}}>DESCRIPTION</th>
                    <th style={{textAlign:"right",padding:"6px 8px",fontSize:11,color:"#8993a4"}}>AMOUNT</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.filter(i=>i.description&&Number(i.amount)>0).map((item,i)=>(
                    <tr key={i} style={{borderBottom:"1px solid #f4f5f7"}}>
                      <td style={{padding:"8px",fontSize:13,color:"#1a1f36"}}>{item.description}</td>
                      <td style={{padding:"8px",fontSize:13,fontWeight:600,textAlign:"right",color:NAVY}}>${Number(item.amount).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{borderTop:"2px solid "+NAVY}}>
                    <td style={{padding:"10px 8px",fontWeight:700,color:NAVY}}>Total</td>
                    <td style={{padding:"10px 8px",fontWeight:800,fontSize:18,textAlign:"right",color:NAVY}}>${total.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>

              {draftInv.stripe_hosted_url&&(
                <a href={draftInv.stripe_hosted_url} target="_blank" rel="noreferrer"
                  style={{fontSize:11,color:"#8993a4",display:"block",marginBottom:12}}>
                  👁 View on Stripe →
                </a>
              )}

              <div style={{background:"#fffae6",border:"1px solid #fde047",borderRadius:6,padding:"8px 12px",fontSize:12,color:"#854d0e",marginBottom:8}}>
                ⚡ Clicking Finalize & Charge will immediately charge ${total.toFixed(2)} to the card on file.
              </div>
              <div style={{fontSize:11,color:"#8993a4"}}>
                Need to make changes? Click ← Edit to go back and modify the line items.
              </div>
            </>
          )}

          {error&&<div style={{background:"#ffebe6",border:"1px solid #ffbdad",borderRadius:6,padding:"8px 12px",fontSize:12,color:"#bf2600",marginTop:12}}>⚠️ {error}</div>}
        </div>
        {/* Phone: the gold action takes a full-width row of its own so long labels
            ("⚡ Finalize & Charge $1,234.56") never clip; a disabled gold button also
            says WHY it's disabled, since .45 opacity doesn't read as disabled on a phone. */}
        <div className="modal-footer" style={isMobile?{flexWrap:"wrap"}:undefined}>
          {isMobile && step===1 && mode==="new" && total<=0 && !saving && (
            <div style={{flex:"1 1 100%",fontSize:12,color:"#8993a4",textAlign:"center"}}>Add a description and amount to continue</div>
          )}
          {isMobile && step===1 && mode==="existing" && !selectedInvoice && (
            <div style={{flex:"1 1 100%",fontSize:12,color:"#8993a4",textAlign:"center"}}>Tap an invoice above to select it</div>
          )}
          <button className="btn btn-ghost" onClick={step===2?async()=>{
            if (draftInv?.stripe_invoice_id) {
              await fetch(`${SUPABASE_URL}/functions/v1/update-invoice`,{
                method:"POST",
                headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
                body:JSON.stringify({action:"void",stripe_invoice_id:draftInv.stripe_invoice_id})
              }).catch(()=>{});
              if (draftInv.id) await fetch(`${SUPABASE_URL}/rest/v1/invoices?id=eq.${draftInv.id}`,{
                method:"DELETE",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
              }).catch(()=>{});
            }
            setDraftInv(null); setStep(1);
          }:onClose}>
            {step===2?"← Edit":"Cancel"}
          </button>
          {step===1 && mode==="existing" && (
            <button className="btn btn-gold" style={goldBtn} onClick={handleChargeExisting} disabled={finalizing || !selectedInvoice}>
              {finalizing
                ? "Charging..."
                : selectedInvoice
                  ? `⚡ Charge $${Number(selectedInvoice.amount).toFixed(2)}`
                  : "Select an invoice"}
            </button>
          )}
          {step===1 && mode==="new" && (
            <button className="btn btn-gold" style={goldBtn} onClick={handleCreateDraft} disabled={saving||total<=0}>
              {saving?"Creating draft...":"Review & Charge →"}
            </button>
          )}
          {step===2 && (
            <button className="btn btn-gold" style={goldBtn} onClick={handleFinalizeAndCharge} disabled={finalizing}>
              {finalizing?"Charging...":"⚡ Finalize & Charge $"+total.toFixed(2)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
